import {
  Box,
  Briefcase,
  Calendar as CalendarIcon,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Edit,
  ExternalLink,
  Folder,
  Loader2,
  Settings,
  Tag as TagIcon,
  Users,
  Zap,
} from 'lucide-react';
import { useCallback, useEffect, useState, type ComponentType, type SVGProps } from 'react';
import { FaRegFilePdf, FaRegFileWord } from 'react-icons/fa6';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../lib/auth-context.js';
import { Button } from '../../../components/ui/button.js';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { pushRecentScenario } from '../../../lib/recent-scenarios.js';
import { cn } from '../../../lib/utils.js';
import { useScenarioDetail, useScenarioPlanningObjects, useUpdateScenario } from '../hooks.js';
import type { ScenarioApplication, ScenarioRow as ScenarioRowData } from '../types.js';
import { formatSchedule } from '../utils.js';
import { ApplicationIcon } from './ApplicationIcon.js';
import { ScenarioMetadataSection, type ScenarioMetadataInput } from './ScenarioMetadataSection.js';
import { ScenarioProcessDiagram } from './ScenarioProcessDiagram.js';
import { ScenarioTagSelector } from './ScenarioTagSelector.js';
import { ScenarioNotesPanel } from './ScenarioNotesPanel.js';
import { TagChip } from './TagChip.js';

/**
 * Scenario detail modal. Mirrors the legacy
 * `wf-audit/src/slices/scenario/components/scenarioModal/Provider.tsx`
 * — the popup that opens when a row's scenario name is clicked.
 *
 * Stage-1 scope (today):
 *   • Title row with role badge + Word/PDF placeholder buttons +
 *     close button.
 *   • Summary grid: Modules + chip strip, Tags, Folder, Schedule,
 *     Teams, Trigger(s).
 *   • Show Notes toggle (visual-only — wired in a follow-up).
 *   • Collapsible `Documentation` section with Description text and
 *     a Documents placeholder.
 *
 * Deferred (legacy modal also has):
 *   • Process diagram thumbnail (`ScenarioProcessDiagram`).
 *   • Word / PDF export.
 *   • Editable description with `DraftEditor` + AI-summary panels.
 *   • `Workfront Metadata` / `Fusion Metadata` / `Other Systems` blocks
 *     — keyed off scenario-detail fields that aren't surfaced on the
 *     list response. Land alongside the rest of Stage 4.
 */
export interface ScenarioDetailModalProps {
  fusionId: string | null;
  /** Listing-row passthrough so the summary block can render
   *  immediately without waiting for the detail fetch. */
  listingRow: ScenarioRowData | undefined;
  onClose: () => void;
  role: 'business' | 'administrator' | 'developer';
  /** Ordered fusionIds of the *currently-filtered* list. Threaded
   *  through so the modal can wire ← / → to walk the same set of
   *  rows the operator just narrowed down — wrap is intentional so
   *  rotating through a small filter set stays cheap. Empty array
   *  hides the prev/next chevrons. */
  siblingFusionIds?: ReadonlyArray<string>;
  /** Replace the URL `?scenarioId=` with the supplied id. Triggered
   *  from the prev/next chevrons + arrow keys; the parent owns the
   *  router state, the modal just announces intent. */
  onNavigate?: (fusionId: string) => void;
}

const ROLE_META: Record<
  ScenarioDetailModalProps['role'],
  { label: string; icon: ComponentType<SVGProps<SVGSVGElement>> }
> = {
  business: { label: 'Business Executive', icon: Briefcase },
  administrator: { label: 'Workfront Administrator', icon: Settings },
  developer: { label: 'Fusion Developer', icon: Zap },
};

export function ScenarioDetailModal({
  fusionId,
  listingRow,
  onClose,
  role,
  siblingFusionIds,
  onNavigate,
}: ScenarioDetailModalProps): JSX.Element {
  // Fetch the rich detail. Listing-row gives us instant content for
  // the summary; the detail fetch may extend it (description,
  // metadata blocks) but isn't blocking the open.
  const { data: detail, isLoading } = useScenarioDetail(fusionId ?? undefined);

  // Planning objects don't ride in the scenario-detail response —
  // they're populated asynchronously by the planning-record-resolution
  // Fargate worker and exposed via a separate license-wide endpoint.
  // Pivot client-side by fusionId so the metadata-section gets the
  // filtered list. Gated on `role !== 'business'` to skip the round-
  // trip in the executive view (matches the metadata section's
  // visibility gate).
  const { byScenario: planningByScenario } = useScenarioPlanningObjects(role !== 'business');
  const planningObjects = fusionId ? (planningByScenario.get(fusionId) ?? []) : [];

  const row = mergeRow(listingRow, detail);
  const open = fusionId !== null;
  // The `role` prop comes from the URL — `/scenarios` = business,
  // `/scenarios/administrator` = admin, `/scenarios/developer` = dev.
  // Switching the view navigates the URL, matching legacy behaviour
  // (`wf-audit/.../ScenarioModalViewSelect.tsx` uses `pushState` +
  // `router.refresh()` for the same effect). Doing it via URL — not
  // local state — means the underlying scenarios list re-renders in
  // the new role too, so the modal's preview matches the page below it.
  //
  // The previous local-state-only approach was a no-op user-facing
  // (operator-reported: "switcher doesn't do anything"). Business
  // operators now get the full segmented control too — legacy parity:
  // the Select dropdown was always all 3 options regardless of role.
  const view = role;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const changeView = useCallback(
    (next: 'business' | 'administrator' | 'developer') => {
      if (next === role) return;
      // Preserve `?scenarioId=…` so the modal stays open on the new
      // role's URL.
      const target = next === 'business' ? '/scenarios' : `/scenarios/${next}`;
      const qs = searchParams.toString();
      navigate(qs ? `${target}?${qs}` : target);
    },
    [role, navigate, searchParams],
  );

  // Each time the modal binds to a new scenario, record a "recently-
  // viewed" entry for the command palette. Falls back to "Scenario
  // <id>" so the palette still has something to display before the
  // detail fetch settles.
  useEffect(() => {
    if (!fusionId) return;
    const displayName = row?.name ?? listingRow?.name ?? `Scenario ${fusionId}`;
    const folderName = row?.folderName ?? listingRow?.folderName ?? undefined;
    pushRecentScenario({ fusionId, name: displayName, folderName: folderName ?? undefined });
  }, [fusionId, row?.name, row?.folderName, listingRow?.name, listingRow?.folderName]);

  const navPosition = useNavPosition(fusionId, siblingFusionIds);
  const goPrev = (): void => {
    if (!navPosition || !onNavigate) return;
    onNavigate(navPosition.prevId);
  };
  const goNext = (): void => {
    if (!navPosition || !onNavigate) return;
    onNavigate(navPosition.nextId);
  };

  // ← / → step through the filtered sibling list while the modal is
  // open. Skip when focus is in an editable field so the description
  // textarea + note composer aren't hijacked.
  useEffect(() => {
    if (!open || !navPosition || !onNavigate) return;
    function onKey(e: KeyboardEvent): void {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isEditableTarget(e.target)) return;
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (navPosition) onNavigate?.(navPosition.nextId);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (navPosition) onNavigate?.(navPosition.prevId);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, navPosition, onNavigate]);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent
        className={cn(
          // Wider than the previous `max-w-5xl` so the process diagram
          // has more horizontal room — matches the legacy modal which
          // dominated the viewport. Cap at the viewport so it scales
          // down on narrower windows.
          'w-[95vw] max-w-[1400px] border-foreground/15 bg-[color:var(--surface-overlay)] text-foreground',
          'max-h-[90vh] overflow-y-auto',
        )}
      >
        <DialogHeader className="flex flex-row items-center gap-3 pb-2">
          <DialogTitle className="flex flex-1 items-center gap-2 text-lg font-medium text-foreground">
            <Zap className="h-5 w-5 text-[color:var(--brand-yellow)]" />
            <span>{row?.name ?? (isLoading ? 'Loading…' : `Scenario ${fusionId}`)}</span>
          </DialogTitle>
          {navPosition && onNavigate ? (
            <div
              className="inline-flex items-center gap-1 rounded-md border border-foreground/15 bg-foreground/5"
              aria-label="Navigate scenarios"
            >
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous scenario"
                className="rounded-l-md px-2 py-1.5 text-foreground/70 transition-colors hover:bg-foreground/10 hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-2 text-xs font-mono text-foreground/50">
                {navPosition.index + 1}/{navPosition.total}
              </span>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next scenario"
                className="rounded-r-md px-2 py-1.5 text-foreground/70 transition-colors hover:bg-foreground/10 hover:text-foreground"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          ) : null}
          {/* Role dropdown — single button showing the current view +
              chevron; click opens a menu listing every role with a
              check next to the active one. Mirrors the legacy
              `ScenarioModalViewSelect.tsx` dropdown shape (pre-2026
              the SPA had a 3-button segmented toggle which read as
              "filter / status pills" to operators; the dropdown is
              the canonical legacy affordance). */}
          <ViewDropdown current={view} onChange={changeView} />
          <DocumentExportButton row={row} format="docx" label="Word" />
          <DocumentExportButton row={row} format="pdf" label="PDF" />
        </DialogHeader>

        <div className="my-2">
          <ScenarioProcessDiagram
            link={row?.screenshotLink ?? null}
            modifiedAt={row?.modifiedAt ?? null}
            scenarioName={row?.name ?? null}
            fusionId={row?.fusionId ?? null}
            isLoading={isLoading}
          />
        </div>

        {row ? <SummaryGrid row={row} fusionId={fusionId} /> : null}

        <DocumentationSection row={row} fusionId={fusionId} view={view} />

        <ScenarioMetadataSection
          data={{ ...((detail ?? {}) as ScenarioMetadataInput), planningObjects }}
          scenarioUrl={row?.url ?? null}
          view={view}
        />

        {fusionId ? <ScenarioNotesPanel scenarioId={fusionId} /> : null}
      </DialogContent>
    </Dialog>
  );
}

interface NavPosition {
  index: number;
  total: number;
  prevId: string;
  nextId: string;
}

/**
 * Compute the prev/next fusionId pair (with wrap) given the active
 * scenario + the filtered sibling list. Returns `null` when the
 * sibling list is empty, has a single entry, or doesn't contain the
 * active scenario (e.g. operator opens a scenario via deep-link
 * whose filters exclude that row).
 */
function useNavPosition(
  fusionId: string | null,
  siblings: ReadonlyArray<string> | undefined,
): NavPosition | null {
  if (!fusionId || !siblings || siblings.length < 2) return null;
  const index = siblings.indexOf(fusionId);
  if (index < 0) return null;
  const total = siblings.length;
  const prevId = siblings[(index - 1 + total) % total]!;
  const nextId = siblings[(index + 1) % total]!;
  return { index, total, prevId, nextId };
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Role dropdown that switches the scenarios page's view. Mirrors the
 * legacy `ScenarioModalViewSelect.tsx` — trigger button shows the
 * current role + chevron, menu lists every role with a check on the
 * active one. Selecting a role calls `onChange`, which navigates the
 * URL (`/scenarios` / `/scenarios/administrator` /
 * `/scenarios/developer`) so the underlying list re-renders in the
 * new role context too.
 */
function ViewDropdown({
  current,
  onChange,
}: {
  current: ScenarioDetailModalProps['role'];
  onChange: (next: ScenarioDetailModalProps['role']) => void;
}): JSX.Element {
  const ROLES: ReadonlyArray<ScenarioDetailModalProps['role']> = [
    'business',
    'administrator',
    'developer',
  ];
  const currentMeta = ROLE_META[current];
  const CurrentIcon = currentMeta.icon;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Scenario view: ${currentMeta.label}`}
          className="inline-flex items-center gap-2 rounded-md border border-foreground/15 bg-foreground/5 px-3 py-1.5 text-sm font-medium text-foreground hover:border-foreground/40"
        >
          <CurrentIcon className="h-3.5 w-3.5 text-[color:var(--brand-yellow)]" />
          <span>{currentMeta.label}</span>
          <ChevronDown className="h-3.5 w-3.5 text-foreground/60" />
        </button>
      </DropdownMenuTrigger>
      {/* DialogContent is `z-[10000]`; the default DropdownMenuContent
          z-index (`z-50`) would render the menu behind the modal,
          making it invisible. Override to land above the modal. */}
      <DropdownMenuContent align="end" className="z-[10001] min-w-[200px]">
        {ROLES.map((r) => {
          const meta = ROLE_META[r];
          const Icon = meta.icon;
          const active = current === r;
          return (
            <DropdownMenuItem
              key={r}
              onSelect={() => onChange(r)}
              className={cn(active && 'bg-foreground/10')}
            >
              <Icon className="h-4 w-4" />
              <span className="flex-1">{meta.label}</span>
              {active ? <Check className="h-4 w-4 text-foreground/70" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

interface MergedRow extends ScenarioRowData {
  /** Tags as the engine returns them on detail (legacy used `tags`).
   *  Includes `id` so the in-modal tag editor can pre-fill its
   *  checkbox state. */
  resolvedTags?: ReadonlyArray<{
    id?: number;
    title: string;
    color?: string | null;
    icon?: string | null;
  }>;
  /** Trigger module info if surfaced by detail. */
  triggerName?: string | null;
}

/**
 * Header button that fetches a fully-templated scenario report from
 * the engine in the requested format and triggers a browser download.
 *
 * Matches the legacy `wf-audit` UX: two buttons side by side,
 * **Word** and **PDF**. Both hit the same endpoint
 * (`GET /v1/scenarios/{fusionId}/document`) with a `?format=docx` or
 * `?format=pdf` query param — server-side renders the docx and (for
 * pdf) converts it via the LibreOffice Lambda layer. The previous
 * client-side `docx` + `jspdf` text-only stub was deleted alongside
 * this change so a single source of truth (the engine + the bundled
 * `scenario_template_input.docx` template) drives both outputs.
 *
 * UX:
 *   • Disabled until the row has loaded — the engine needs the
 *     fusionId, and emitting a `?fusionId=undefined` request would
 *     just 400.
 *   • Spinner + format-specific "Generating…" label while the
 *     request is in flight. Word: 3-5s warm; PDF: 6-10s warm,
 *     up to ~15s on cold (LibreOffice unpack).
 *   • Errors surfaced as toasts. The engine returns JSON
 *     `{ error: '...' }` on failure; `ApiError.body` carries that
 *     through and we surface the message verbatim.
 *
 * Filename comes from the engine's `Content-Disposition` header
 * (ASCII-only, no RFC-5987 encoding) — falls back to a synthesised
 * `<scenario-name>.<ext>` name when the header is stripped by a proxy.
 */
function DocumentExportButton({
  row,
  format,
  label,
}: {
  row: MergedRow | undefined;
  format: 'docx' | 'pdf';
  label: string;
}): JSX.Element {
  const { api } = useAuth();
  const [generating, setGenerating] = useState(false);
  async function handleClick(): Promise<void> {
    if (!row?.fusionId || generating) return;
    setGenerating(true);
    try {
      const { blob, filename } = await api.getBlob(
        `/v1/scenarios/${encodeURIComponent(row.fusionId)}/document?format=${format}`,
      );
      const url = URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        const safe = (row.name || 'scenario').replace(/[^\w.-]+/g, '_').slice(0, 80);
        a.download = filename ?? `${safe}.${format}`;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : typeof err === 'string' ? err : 'Unknown error';
      toast.error(`Failed to generate ${label}: ${message}`);
    } finally {
      setGenerating(false);
    }
  }
  const FormatIcon = format === 'docx' ? FaRegFileWord : FaRegFilePdf;
  const inFlightLabel = format === 'docx' ? 'Generating…' : 'Converting…';
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={handleClick}
      disabled={!row?.fusionId || generating}
      className="border-foreground/15 text-foreground/70 hover:border-foreground/40 hover:text-foreground"
      title={`Download this scenario as a .${format}`}
    >
      {generating ? (
        <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />
      ) : (
        <FormatIcon className="mr-1.5 h-4 w-4" aria-hidden />
      )}
      {generating ? inFlightLabel : label}
    </Button>
  );
}

function mergeRow(
  listing: ScenarioRowData | undefined,
  detail: Record<string, unknown> | undefined,
): MergedRow | undefined {
  if (!listing && !detail) return undefined;
  const base = (listing ?? {}) as MergedRow;
  if (!detail) return base;
  const merged: MergedRow = { ...base, ...(detail as Partial<MergedRow>) };
  const tags = (detail.tags ?? listing?.tags) as
    | ReadonlyArray<{ id?: number; title: string; color?: string | null; icon?: string | null }>
    | undefined;
  if (tags) merged.resolvedTags = tags;
  const trigger = detail.triggerInfo as { fusionModuleName?: string | null } | null | undefined;
  if (trigger && typeof trigger.fusionModuleName === 'string') {
    merged.triggerName = trigger.fusionModuleName;
  }
  return merged;
}

function SummaryGrid({ row, fusionId }: { row: MergedRow; fusionId: string | null }): JSX.Element {
  const apps = row.applications ?? [];
  const schedule = formatSchedule(row.eventSchedule ?? undefined);
  const currentTagIds = (row.resolvedTags ?? [])
    .map((t) => t.id)
    .filter((id): id is number => typeof id === 'number');
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <SummaryBox icon={Box} label="Modules">
        <div className="flex items-center gap-2">
          <span className="text-base font-semibold text-foreground">{row.totalModules ?? 0}</span>
          <div className="flex items-center -space-x-1.5">
            {apps.slice(0, 6).map((app: ScenarioApplication, i: number) => (
              <ApplicationIcon key={i} app={app} size={28} sizeIcon={20} />
            ))}
          </div>
        </div>
      </SummaryBox>
      <SummaryBox icon={Users} label="Teams">
        <span className="text-sm text-foreground/70">
          {row.teamFusionId ? `Team ${row.teamFusionId}` : '—'}
        </span>
      </SummaryBox>
      <SummaryBox
        icon={TagIcon}
        label="Tags"
        action={
          fusionId ? (
            <ScenarioTagSelector fusionId={fusionId} currentTagIds={currentTagIds} />
          ) : null
        }
      >
        {row.resolvedTags && row.resolvedTags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {row.resolvedTags.map((t, i) => (
              <TagChip
                key={i}
                tag={t as { title: string; color?: string | null; icon?: string | null }}
                size="md"
              />
            ))}
          </div>
        ) : (
          <span className="text-sm text-foreground/40">—</span>
        )}
      </SummaryBox>
      <SummaryBox icon={Folder} label="Folder">
        <span className="text-sm text-foreground/80">{row.folderName || '—'}</span>
      </SummaryBox>
      <SummaryBox icon={CalendarIcon} label="Schedule">
        <span className="text-sm text-foreground/80">{schedule || 'No schedule'}</span>
      </SummaryBox>
      <SummaryBox icon={Zap} label="Trigger(s)">
        <span className="text-sm text-foreground/80">{row.triggerName || '—'}</span>
      </SummaryBox>
    </div>
  );
}

function SummaryBox({
  icon: Icon,
  label,
  children,
  action,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  children: React.ReactNode;
  /** Optional right-aligned slot for an action button — e.g. the
   *  Tags box surfaces an "Edit" trigger that opens a tag-picker
   *  popover. */
  action?: React.ReactNode;
}): JSX.Element {
  return (
    <div className="rounded-md border border-foreground/10 bg-foreground/[0.03] p-3">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 text-xs uppercase tracking-wide text-foreground/50">
          <Icon className="h-3.5 w-3.5" /> {label}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      <div>{children}</div>
    </div>
  );
}

function DocumentationSection({
  row,
  fusionId,
  view,
}: {
  row: MergedRow | undefined;
  fusionId: string | null;
  view: ScenarioDetailModalProps['role'];
}): JSX.Element {
  const [open, setOpen] = useState(true);
  return (
    <section className="rounded-md border border-foreground/10">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 border-b border-foreground/10 px-3 py-2 text-left text-sm font-medium uppercase tracking-wider text-foreground/80"
      >
        {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        Documentation
      </button>
      {open ? (
        <div className="space-y-4 p-4">
          <DescriptionEditor fusionId={fusionId} initialValue={row?.description ?? ''} />
          <article>
            <header className="mb-1 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Documents</h3>
              <button
                type="button"
                disabled
                className="text-foreground/30 hover:text-foreground/60"
                aria-label="Edit documents"
              >
                <Edit className="h-4 w-4" />
              </button>
            </header>
            <p className="text-sm italic text-foreground/40">No documents found</p>
          </article>
          {row?.url && view !== 'business' ? (
            // Business Executive view hides the raw Fusion link —
            // execs don't need to be one click from the developer
            // console. Admins + developers see it.
            <a
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-blue-700 dark:text-blue-300 underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              Open scenario in Fusion <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * Inline description editor. Default state is a read-only paragraph
 * + a pencil affordance; clicking the pencil flips to a textarea
 * with Save / Cancel buttons. Save fires
 * `useUpdateScenario(fusionId).update({ description })` against the
 * engine's PATCH route. Optimistic-update is left to TanStack
 * Query's `onSuccess` invalidator so the surrounding list/detail
 * caches refetch automatically.
 */
function DescriptionEditor({
  fusionId,
  initialValue,
}: {
  fusionId: string | null;
  initialValue: string;
}): JSX.Element {
  const { update, isPending, error } = useUpdateScenario(fusionId ?? undefined);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialValue);
  const trimmed = initialValue.trim();

  async function save(): Promise<void> {
    const next = draft.trim();
    if (next === trimmed) {
      setEditing(false);
      return;
    }
    try {
      await update({ description: next });
      setEditing(false);
    } catch {
      /* error surfaces via `error` below */
    }
  }

  return (
    <article>
      <header className="mb-1 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground">Description</h3>
        {!editing && fusionId ? (
          <button
            type="button"
            onClick={() => {
              setDraft(initialValue);
              setEditing(true);
            }}
            className="text-foreground/40 transition-colors hover:text-foreground/80"
            aria-label="Edit description"
          >
            <Edit className="h-4 w-4" />
          </button>
        ) : null}
      </header>
      {editing ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            className="w-full rounded-md border border-foreground/15 bg-foreground/5 px-3 py-2 text-sm text-foreground placeholder:text-foreground/40 focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-white/20"
            placeholder="Describe what this scenario does, the business value it delivers, and any operator caveats…"
            autoFocus
          />
          {error ? (
            <div className="text-xs text-rose-700 dark:text-rose-300">
              Save failed: {error.message}
            </div>
          ) : null}
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setEditing(false)}
              className="border-foreground/15 text-foreground/70"
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => void save()}
              disabled={isPending}
              className="bg-[color:var(--brand-yellow)] text-[color:var(--gradient-end)] hover:opacity-90"
            >
              {isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/80">
          {trimmed.length > 0 ? (
            trimmed
          ) : (
            <span className="italic text-foreground/40">No description set.</span>
          )}
        </p>
      )}
    </article>
  );
}
